import React, { useState } from "react";
import {
	Plus,
	Edit3,
	Trash2,
	Eye,
	EyeOff,
	Video,
	ExternalLink,
	Calendar,
	Clock,
	User,
	ChevronDown,
	ChevronUp,
	Wand2,
} from "lucide-react";
import CustomModal from "./CustomModal";

const formatDate = (dateStr) => {
	if (!dateStr) return "—";
	return new Date(dateStr).toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
};

const TrainerWorkshops = ({
	workshops,
	onAdd,
	onUpdate,
	onDelete,
	onTogglePublish,
	loading,
	category = "workshop",
	label = "Workshop",
	labelPlural = "Workshops",
}) => {
	const [showForm, setShowForm] = useState(false);
	const [editingWorkshop, setEditingWorkshop] = useState(null);
	const [expandedWorkshop, setExpandedWorkshop] = useState(null);
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		videoUrl: "",
		instructor: "",
		workshopDate: "",
		duration: "",
	});

	// Videos belonging to this tab's category. Legacy docs without a
	// category are treated as "workshop" so nothing is orphaned.
	const categoryWorkshops = workshops.filter(
		(w) => (w.category || "workshop") === category,
	);
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "delete",
		onConfirm: null,
	});

	const resetForm = () => {
		setFormData({
			title: "",
			description: "",
			videoUrl: "",
			instructor: "",
			workshopDate: "",
			duration: "",
		});
		setEditingWorkshop(null);
		setShowForm(false);
	};

	const startEdit = (workshop) => {
		setEditingWorkshop(workshop);
		setFormData({
			title: workshop.title || "",
			description: workshop.description || "",
			videoUrl: workshop.videoUrl || "",
			instructor: workshop.instructor || "",
			workshopDate: workshop.workshopDate || "",
			duration: workshop.duration || "",
		});
		setShowForm(true);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!formData.title.trim() || !formData.videoUrl.trim()) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Title and video URL are required.",
				type: "warning",
			});
			return;
		}

		try {
			if (editingWorkshop) {
				await onUpdate(editingWorkshop.id, { ...formData, category });
				setModal({
					show: true,
					title: `${label} Updated`,
					message: `${label} updated successfully!`,
					type: "success",
				});
			} else {
				await onAdd({ ...formData, category });
				setModal({
					show: true,
					title: `${label} Added`,
					message: `${label} added successfully!`,
					type: "success",
				});
			}
			resetForm();
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to save workshop.",
				type: "error",
			});
		}
	};

	const handleDelete = (workshop) => {
		setConfirmModal({
			show: true,
			title: `Delete ${label}`,
			message: `Are you sure you want to delete "${workshop.title}"? This cannot be undone.`,
			type: "delete",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await onDelete(workshop.id);
					setModal({
						show: true,
						title: "Deleted",
						message: "Workshop deleted successfully!",
						type: "success",
					});
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message || "Failed to delete workshop.",
						type: "error",
					});
				}
			},
		});
	};

	const handleTogglePublish = async (workshop, e) => {
		if (e) e.stopPropagation();
		try {
			await onTogglePublish(workshop.id, !workshop.published);
			setModal({
				show: true,
				title: workshop.published ? "Unpublished" : "Published",
				message: workshop.published
					? `"${workshop.title}" is now hidden from users.`
					: `"${workshop.title}" is now visible to users!`,
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to update workshop.",
				type: "error",
			});
		}
	};

	const toggleExpanded = (id) => {
		setExpandedWorkshop((prev) => (prev === id ? null : id));
	};

	return (
		<div className="card" style={{ overflow: "hidden" }}>
			<div className="card-header d-flex justify-content-between align-items-center">
				<h5 className="mb-0">
					<Video size={20} className="me-2" />
					{labelPlural}
				</h5>
				<button
					className="btn btn-primary btn-sm"
					onClick={() => {
						if (showForm) {
							resetForm();
						} else {
							setShowForm(true);
						}
					}}
					disabled={loading}
				>
					{showForm ? (
						"Cancel"
					) : (
						<>
							<Plus size={16} className="me-1" />
							Add
						</>
					)}
				</button>
			</div>

			<div className="card-body" style={{ overflow: "hidden" }}>
				{/* Add / Edit Form */}
				{showForm && (
					<div className="mb-4 p-3 bg-light rounded workshop-form-mobile">
						<h6>{editingWorkshop ? `Edit ${label}` : `Add New ${label}`}</h6>
						<form onSubmit={handleSubmit}>
							<div className="row g-3">
								<div className="col-12 col-md-6">
									<label className="form-label">Title *</label>
									<input
										type="text"
										className="form-control"
										value={formData.title}
										onChange={(e) =>
											setFormData({ ...formData, title: e.target.value })
										}
										placeholder="e.g. Mobility Workshop"
										disabled={loading}
										required
									/>
								</div>
								<div className="col-12 col-md-6">
									<label className="form-label">Video URL *</label>
									<input
										type="url"
										className="form-control"
										value={formData.videoUrl}
										onChange={(e) =>
											setFormData({ ...formData, videoUrl: e.target.value })
										}
										placeholder="e.g. https://youtu.be/Gm_jDljxJuQ"
										disabled={loading}
										required
									/>
								</div>
								<div className="col-12 col-md-4">
									<label className="form-label">Instructor</label>
									<input
										type="text"
										className="form-control"
										value={formData.instructor}
										onChange={(e) =>
											setFormData({
												...formData,
												instructor: e.target.value,
											})
										}
										placeholder="Who ran the workshop?"
										disabled={loading}
									/>
								</div>
								<div className="col-6 col-md-4">
									<label className="form-label">Date</label>
									<input
										type="date"
										className="form-control"
										value={formData.workshopDate}
										onChange={(e) =>
											setFormData({
												...formData,
												workshopDate: e.target.value,
											})
										}
										disabled={loading}
									/>
								</div>
								<div className="col-6 col-md-4">
									<label className="form-label">Duration</label>
									<input
										type="text"
										className="form-control"
										value={formData.duration}
										onChange={(e) =>
											setFormData({ ...formData, duration: e.target.value })
										}
										placeholder="e.g. 45 mins"
										disabled={loading}
									/>
								</div>
								<div className="col-12">
									<label className="form-label">Description</label>
									<textarea
										className="form-control"
										rows="3"
										value={formData.description}
										onChange={(e) =>
											setFormData({
												...formData,
												description: e.target.value,
											})
										}
										placeholder="Brief description of the workshop content..."
										disabled={loading}
									/>
								</div>
								<div className="col-12">
									<div className="d-flex gap-2">
										<button
											type="submit"
											className="btn btn-success flex-grow-1 flex-md-grow-0"
											disabled={loading}
										>
											{loading
												? "Saving..."
												: editingWorkshop
													? `Update ${label}`
													: `Add ${label}`}
										</button>
										<button
											type="button"
											className="btn btn-secondary flex-grow-1 flex-md-grow-0"
											onClick={resetForm}
											disabled={loading}
										>
											Cancel
										</button>
									</div>
								</div>
							</div>
						</form>
					</div>
				)}

				{/* Workshops List */}
				<h6 className="mb-3">
					All {labelPlural} ({loading ? "..." : categoryWorkshops.length})
				</h6>

				{loading ? (
					<div className="text-center py-4">
						<div className="spinner-border text-primary" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
					</div>
				) : categoryWorkshops.length === 0 ? (
					<div className="text-center py-5">
						<Video size={48} style={{ opacity: 0.3 }} className="mb-3" />
						<p className="text-muted">No {labelPlural.toLowerCase()} yet.</p>
						<p className="text-muted">
							Add your first {label.toLowerCase()} recording to share with your
							members!
						</p>
					</div>
				) : (
					<div>
						{categoryWorkshops.map((workshop) => {
							const isExpanded = expandedWorkshop === workshop.id;

							return (
								<div
									key={workshop.id}
									className="mb-3"
									style={{
										borderRadius: "8px",
										overflow: "hidden",
										border: isExpanded ? "1px solid #d15a36" : "1px solid #333",
										backgroundColor: "#1a1a1a",
										transition: "border-color 0.2s ease",
									}}
								>
									{/* Workshop Header */}
									<div
										className="p-3"
										onClick={() => toggleExpanded(workshop.id)}
										style={{
											cursor: "pointer",
											borderBottom: isExpanded ? "2px solid #d15a36" : "none",
										}}
									>
										{/* Title + status row */}
										<div className="d-flex justify-content-between align-items-start gap-2 mb-1">
											<div style={{ flex: 1, minWidth: 0 }}>
												<h6
													className="mb-0"
													style={{
														color: "#fff",
														wordBreak: "break-word",
														display: "flex",
														alignItems: "flex-start",
														gap: "0.5rem",
													}}
												>
													<Video
														size={16}
														style={{
															color: "#d15a36",
															flexShrink: 0,
															marginTop: "3px",
														}}
													/>
													<span>{workshop.title}</span>
												</h6>
											</div>

											<div
												className="d-flex align-items-center gap-1"
												style={{ flexShrink: 0 }}
											>
												<span
													className={`badge ${workshop.published ? "bg-success" : "bg-secondary"}`}
													style={{
														fontSize: "0.7rem",
														cursor: "pointer",
													}}
													onClick={(e) => handleTogglePublish(workshop, e)}
													title={
														workshop.published
															? "Click to unpublish"
															: "Click to publish"
													}
												>
													{workshop.published ? "Live" : "Draft"}
												</span>
												{isExpanded ? (
													<ChevronUp size={18} style={{ color: "#d15a36" }} />
												) : (
													<ChevronDown
														size={18}
														style={{
															color: "rgba(255,255,255,0.5)",
														}}
													/>
												)}
											</div>
										</div>

										{/* Description preview */}
										{workshop.description && (
											<p
												className="mb-2"
												style={{
													color: "rgba(255,255,255,0.5)",
													fontSize: "0.85rem",
													lineHeight: 1.4,
													display: "-webkit-box",
													WebkitLineClamp: 2,
													WebkitBoxOrient: "vertical",
													overflow: "hidden",
													margin: 0,
													marginBottom: "0.5rem",
													wordBreak: "break-word",
												}}
											>
												{workshop.description}
											</p>
										)}

										{/* Meta badges */}
										<div className="d-flex flex-wrap align-items-center gap-2">
											{workshop.instructor && (
												<span
													className="badge"
													style={{
														backgroundColor: "#d15a36",
														fontSize: "0.75rem",
													}}
												>
													<User
														size={10}
														className="me-1"
														style={{ marginTop: "-1px" }}
													/>
													{workshop.instructor}
												</span>
											)}
											{workshop.workshopDate && (
												<small
													style={{
														color: "rgba(255,255,255,0.45)",
													}}
												>
													<Calendar size={12} className="me-1" />
													{formatDate(workshop.workshopDate)}
												</small>
											)}
											{workshop.duration && (
												<small
													style={{
														color: "rgba(255,255,255,0.45)",
													}}
												>
													<Clock size={12} className="me-1" />
													{workshop.duration}
												</small>
											)}
										</div>
									</div>

									{/* Expanded: description + actions */}
									{isExpanded && (
										<div className="p-3" style={{ overflow: "hidden" }}>
											{/* Video URL display */}
											<div
												className="mb-3 p-2"
												style={{
													backgroundColor: "#2a2a2a",
													borderRadius: "6px",
													border: "1px solid #333",
													overflow: "hidden",
												}}
											>
												<div className="d-flex align-items-center gap-2">
													<ExternalLink
														size={14}
														style={{
															color: "rgba(255,255,255,0.4)",
															flexShrink: 0,
														}}
													/>
													<a
														href={workshop.videoUrl}
														target="_blank"
														rel="noopener noreferrer"
														style={{
															color: "#d15a36",
															fontSize: "0.8rem",
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
															display: "block",
															minWidth: 0,
														}}
													>
														{workshop.videoUrl}
													</a>
												</div>
											</div>

											{/* Action buttons — stack on mobile */}
											<div className="d-flex flex-wrap gap-2">
												<a
													href={workshop.videoUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="btn btn-sm"
													style={{
														backgroundColor: "#d15a36",
														color: "#fff",
														border: "none",
													}}
												>
													<ExternalLink size={14} className="me-1" />
													Open Video
												</a>
												<button
													className="btn btn-sm"
													onClick={(e) => {
														e.stopPropagation();
														startEdit(workshop);
													}}
													disabled={loading}
													style={{
														backgroundColor: "rgba(255,255,255,0.1)",
														color: "#fff",
														border: "none",
													}}
												>
													<Edit3 size={14} className="me-1" />
													Edit
												</button>
												<button
													className="btn btn-sm"
													onClick={(e) => {
														e.stopPropagation();
														handleTogglePublish(workshop, e);
													}}
													disabled={loading}
													style={{
														backgroundColor: "rgba(255,255,255,0.1)",
														color: "#fff",
														border: "none",
													}}
												>
													{workshop.published ? (
														<>
															<EyeOff size={14} className="me-1" />
															Unpublish
														</>
													) : (
														<>
															<Eye size={14} className="me-1" />
															Publish
														</>
													)}
												</button>
												<button
													className="btn btn-sm"
													onClick={(e) => {
														e.stopPropagation();
														handleDelete(workshop);
													}}
													disabled={loading}
													style={{
														backgroundColor: "rgba(220,53,69,0.15)",
														color: "#dc3545",
														border: "none",
													}}
												>
													<Trash2 size={14} className="me-1" />
													Delete
												</button>
											</div>
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>

			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
			<CustomModal
				show={confirmModal.show}
				title={confirmModal.title}
				message={confirmModal.message}
				type={confirmModal.type}
				onConfirm={confirmModal.onConfirm}
				onClose={() => setConfirmModal({ ...confirmModal, show: false })}
				confirmText="Delete"
				loading={loading}
			/>
		</div>
	);
};

export default TrainerWorkshops;
